import classNames from 'classnames';
import { MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import TableRow from '@alltrails/denali/components/TableRow';
import type { AlgoliaSearchResult } from '@alltrails/types/AlgoliaResults';
import { getLocation, getMapTrackLocation, getResultTypeStr } from '../../utils/algoliaResultUtils';
import getBoldedTextElements from '../../utils/getBoldedTextElements';
import ResultIcon from './ResultIcon';
import styles from './styles/styles.module.scss';

const getDescription = (result: AlgoliaSearchResult, resultTypeLabel?: string, hideResultType?: boolean) => {
  const description =
    result.type === 'list' || result.type === 'guide' || result.type === 'page' || result.type === 'map' || result.type === 'track'
      ? [resultTypeLabel]
      : [];
  switch (result.type) {
    case 'map':
    case 'track':
      description.push(getMapTrackLocation(result));
      break;
    case 'area':
    case 'place':
    case 'poi':
    case 'trail':
      description.push(getLocation(result, hideResultType));
      break;
    case 'list':
      description.push('AllTrails');
      break;
    case 'guide':
      description.push('AllTrails+');
      break;
    case 'page':
      if (result.subtype === 'guides') {
        description.push('AllTrails+');
      }
      break;
    case 'filter':
    default:
      return undefined;
  }
  return description.filter(str => !!str).join(' • ');
};

const onLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
  event.preventDefault();
};

export type AlgoliaSearchResultContentProps = {
  hideResultType?: boolean;
  result: AlgoliaSearchResult;
  url?: string;
  query: string;
};

const AlgoliaSearchResultContent = ({ hideResultType, result, url, query }: AlgoliaSearchResultContentProps) => {
  const intl = useIntl();

  const algoliaResult = result as AlgoliaSearchResult;
  // Since a poi has a "subtype" that gets displayed, always show the result type for a poi
  const resultType = hideResultType && algoliaResult.type !== 'poi' ? undefined : getResultTypeStr(algoliaResult, intl);
  const label = getBoldedTextElements(algoliaResult.name, query);
  // Since a poi has a "subtype" that gets displayed, always show the result type for a poi
  const description = getBoldedTextElements(getDescription(algoliaResult, resultType, hideResultType && algoliaResult.type !== 'poi') || '', query);

  const icon = (
    <div className={styles.iconContainer}>
      <ResultIcon result={result} />
    </div>
  );

  const content = (
    <TableRow
      className={classNames(styles.listItemContent, { [styles.noDescription]: !description })}
      leftContent={icon}
      label={<div className={styles.label}>{label}</div>}
      description={
        description ? (
          <div className={styles.description}>
            <span>{description}</span>
          </div>
        ) : null
      }
      testId={`algolia-search-${algoliaResult.name}`}
    />
  );

  // The url is just to get link niceties like "open in new tab" behavior - normal navigation must be handled by the onSelect
  return url ? (
    <a href={url} tabIndex={-1} onClick={onLinkClick}>
      {content}
    </a>
  ) : (
    content
  );
};

export default AlgoliaSearchResultContent;
